import {
  type LotoPickMode,
  pickModeToColor,
} from 'features/lotoReports/lotoTargetEquipment'
import styled, { css } from 'styled-components'

export const LotoPanelWrap = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-self: stretch;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-bottom: 0.0625rem solid var(--border);
  color: var(--foreground);
`

export const LotoPanelBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem 0;
`

export const LotoPanelHeader = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  gap: 0.25rem;
  container-type: inline-size;
`

export const LotoPanelHeading = styled.h2`
  display: flex;
  overflow: hidden;
  align-items: baseline;
  gap: 0.375rem;
  min-width: 0;
  margin: 0;
`

export const LotoPanelTitle = styled.span`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  min-width: 0;
`

export const LotoPanelScroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  gap: 0.75rem;
  min-height: 0;
  margin: 0 -0.75rem;
  padding: 0 0.75rem 0.75rem;
  overflow-x: hidden;
  overflow-y: auto;
`

export const LotoPointsHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-height: 1.75rem;
  background-color: var(--card);
`

export const LotoReportOptionText = styled.span`
  [data-slot='select-content'] & {
    min-width: 0;
    max-width: calc(var(--radix-select-trigger-width) - 3rem);
    overflow-wrap: anywhere;
    white-space: normal;
  }
`

export const LotoEditorLinkSlot = styled.div`
  @container (max-width: 18rem) {
    grid-row: 2;
    grid-column: 1;
  }
`

export const LotoStatusActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.375rem;
  margin-left: auto;
`

export const LotoComboboxSlot = styled.div`
  position: relative;
  container-type: inline-size;
  min-width: 0;
`

export const LotoComboboxClearSlot = styled.div`
  position: absolute;
  top: 50%;
  right: 1.875rem;
  display: flex;
  transform: translateY(-50%);
`

export const LotoComboboxText = styled.span<{ $clearable?: boolean }>`
  display: block;
  width: calc(100cqi - 3.125rem);
  padding-right: ${({ $clearable }) => ($clearable ? '1.5rem' : '0')};
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const LotoPointList = styled.ol<{ $togglable: boolean }>`
  --loto-remove-slot: 1.5rem;
  --loto-toggle-slot: ${({ $togglable }) => ($togglable ? '1.5rem' : '0rem')};
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin: 0;
  padding: 0;
  list-style: none;
  container-type: inline-size;
`

const expandedRowLayout = css`
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    'lead valve actions'
    'detail detail detail'
    'position position position';
  padding: 0.375rem 0.375rem 0.5rem;
  background-color: var(--background);

  @container (min-width: 36rem) {
    grid-template-columns: auto minmax(0, 1fr) minmax(0, 16rem) auto;
    grid-template-areas:
      'lead valve position actions'
      'detail detail position actions';
    padding-bottom: 0.375rem;
  }
`

const collapsedRowLayout = css`
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas: 'lead valve actions';
  padding: 0.125rem 0.375rem;
  background-color: var(--muted);
  color: var(--muted-foreground);
`

export const LotoPointRowGrid = styled.li.attrs<{
  $transform: string | undefined
  $transition: string | undefined
}>(({ $transform, $transition }) => ({
  style: { transform: $transform, transition: $transition },
}))<{ $onThisPage: boolean; $collapsed: boolean; $dragging: boolean }>`
  position: relative;
  display: grid;
  align-items: start;
  column-gap: 0.375rem;
  row-gap: 0;
  min-width: 0;
  border: 0.0625rem solid var(--border);
  border-left: 0.1875rem solid
    ${({ $onThisPage }) =>
      $onThisPage ? 'var(--pnid-isolation-point)' : 'var(--border)'};
  border-radius: 0.375rem;
  z-index: ${({ $dragging }) => ($dragging ? 1 : 'auto')};
  box-shadow: ${({ $dragging }) =>
    $dragging ? '0 0.5rem 1.5rem -0.5rem var(--foreground)' : 'none'};
  outline: ${({ $dragging }) =>
    $dragging ? '0.125rem solid var(--primary)' : 'none'};
  ${({ $collapsed }) => ($collapsed ? collapsedRowLayout : expandedRowLayout)}
`

export const LotoPointLead = styled.div`
  grid-area: lead;
  display: flex;
  align-items: center;
  min-height: 1.5rem;
`

function orderColor(tone: 'accent' | 'muted' | 'default'): string {
  if (tone === 'accent') return 'var(--pnid-isolation-point)'
  if (tone === 'muted') return 'var(--muted-foreground)'
  return 'var(--foreground)'
}

export const LotoPointOrder = styled.span<{
  $tone: 'accent' | 'muted' | 'default'
}>`
  min-width: 1.25rem;
  color: ${({ $tone }) => orderColor($tone)};
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  text-align: right;
`

export const LotoVisuallyHidden = styled.span`
  position: absolute;
  width: 0.0625rem;
  height: 0.0625rem;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`

export const LotoPointValve = styled.div`
  grid-area: valve;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.375rem;
  min-width: 0;
  min-height: 1.5rem;
`

export const LotoPointTagHeading = styled.h4`
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  overflow-wrap: anywhere;
`

export const LotoPointSecondary = styled.span`
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
`

export const LotoPointDetail = styled.div`
  grid-area: detail;
  display: flex;
  min-width: 0;
  margin-top: 0.125rem;
`

export const LotoPointPageLink = styled.button`
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  padding: 0;
  border: 0;
  border-radius: 0.125rem;
  background: none;
  color: var(--muted-foreground);
  font: inherit;
  text-align: left;
  text-underline-offset: 0.25rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 0.125rem solid var(--ring);
    outline-offset: 0.125rem;
  }
`

export const LotoPointFocusButton = styled.button`
  display: flex;
  min-width: 0;
  max-width: 100%;
  padding: 0;
  border: 0;
  border-radius: 0.125rem;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 0.125rem solid var(--ring);
    outline-offset: 0.125rem;
  }

  &:disabled {
    cursor: default;
  }
`

export const LotoPointPosition = styled.div`
  grid-area: position;
  min-width: 0;
  margin-top: 0.375rem;

  @container (min-width: 36rem) {
    margin-top: 0;
  }
`

export const LotoPointActions = styled.div`
  grid-area: actions;
  display: grid;
  grid-template-columns: var(--loto-remove-slot) var(--loto-toggle-slot);
  grid-template-areas: 'remove toggle';
  align-items: center;
  min-height: 1.5rem;
`

export const LotoPointRemoveSlot = styled.div`
  grid-area: remove;
  display: flex;
`

export const LotoPointToggleSlot = styled.div`
  grid-area: toggle;
  display: flex;
`

export const LotoTargetList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin: 0;
  padding: 0;
  list-style: none;
`

export const LotoTargetRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.375rem;
  border: 0.0625rem solid var(--border);
  border-left: 0.1875rem solid var(--pnid-target-equipment);
  border-radius: 0.375rem;
  background-color: var(--background);
`

export const LotoTargetHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
`

export const LotoTargetText = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  overflow-wrap: anywhere;
`

export const LotoModeSwatch = styled.span<{ $mode: LotoPickMode }>`
  flex: none;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 0.125rem;
  background-color: ${({ $mode }) => pickModeToColor[$mode]};
`

export const LotoSectionTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
`

export const LotoEquipmentPickSlot = styled.div`
  display: flex;

  & [data-slot='toggle'][data-state='on'] {
    border-color: var(--pnid-target-equipment);
    background-color: color-mix(
      in oklch,
      var(--pnid-target-equipment) 25%,
      transparent
    );
    color: var(--foreground);
  }
`

export const LotoEquipmentPickHint = styled.div`
  padding: 0.25rem 0.5rem;
  border-left: 0.1875rem solid var(--pnid-target-equipment);
  border-radius: 0.25rem;
  background-color: color-mix(
    in oklch,
    var(--pnid-target-equipment) 12%,
    transparent
  );
`

export const LotoPointsSection = styled.div`
  display: flex;
  flex-direction: column;
`

export const LotoTargetDrawingList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin: 0;
  padding: 0 0 0 0.5rem;
  border-left: 0.0625rem dashed var(--pnid-target-equipment);
  list-style: none;
`

export const LotoTargetDrawingButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 2.25rem;
  padding: 0.25rem 0.375rem;
  border: 0;
  border-radius: 0.25rem;
  background: none;
  color: var(--foreground);
  font: inherit;
  text-align: left;
  cursor: pointer;

  & > svg {
    flex: none;
    width: 0.875rem;
    height: 0.875rem;
    color: var(--muted-foreground);
  }

  &:hover:not(:disabled) {
    background-color: var(--muted);
  }

  &:focus-visible {
    outline: 0.125rem solid var(--ring);
    outline-offset: -0.125rem;
  }

  &[aria-current='page'] {
    background-color: color-mix(
      in oklch,
      var(--pnid-target-equipment) 14%,
      transparent
    );
    cursor: default;
  }
`

export const LotoTargetDrawingText = styled.span`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
`

export const LotoTargetDrawingHere = styled.span`
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 0.25rem;

  & > svg {
    width: 0.875rem;
    height: 0.875rem;
    color: var(--pnid-target-equipment);
  }
`
